import {
    GridFilterType,
    GridRequestType,
    GridSortType,
    TableColumnType,
    TablePropsOptionsType,
} from 'src/types/table';
import { PRODUCT_TRASH_SOURCE } from 'skladon-types';

export interface ProductTrashRow {
    ID: number;
    CREATED_AT: string;
    EVENT_TYPE: string;
    SOURCE: string;
    SKU: string | null;
    EAN: string | null;
    MESSAGE: string;
    RESOLVED_AT: string | null;
}

export interface ProductTrashGridResponse {
    data?: ProductTrashRow[];
    totalCount?: number;
}

export const PRODUCT_TRASH_RESOLVED_COLUMN = 'RESOLVED';
export const PRODUCT_TRASH_RESOLVED_AT_COLUMN = 'RESOLVED_AT';

export const PRODUCT_TRASH_EVENT_TYPES = [
    'EAN_ALREADY_EXISTS',
    'SKU_CHANGE_REJECTED',
    'EAN_CONFLICT_DCIX_SYNC',
    'DUPLICATE_SKU_REJECTED',
    'EAN_WRITE_BLOCKED_BY_DCIX_LOCK',
    'PER_ESHOP_PROPAGATION_SKIPPED',
];

export const PRODUCT_TRASH_SOURCE_VALUES = Object.values(PRODUCT_TRASH_SOURCE);

export const allColumns: TableColumnType[] = [
    { key: 'CREATED_AT', label: 'createdAt', type: 'dateTime' },
    { key: 'SOURCE', label: 'source', type: 'select', selectOption: 'productTrashSourceCodes' },
    { key: 'EVENT_TYPE', label: 'eventType', type: 'select', selectOption: 'productTrashEventTypeCodes' },
    { key: 'SKU', label: 'sku' },
    { key: 'EAN', label: 'ean' },
    { key: 'MESSAGE', label: 'message' },
    {
        key: PRODUCT_TRASH_RESOLVED_COLUMN,
        label: 'resolved',
        type: 'select',
        selectOption: 'productTrashResolvedCodes',
        customRender: true,
    },
];

const REQUIRED_PRODUCT_TRASH_GRID_COLUMNS = ['ID', PRODUCT_TRASH_RESOLVED_AT_COLUMN];

const unique = (items: string[]): string[] => items.filter((item, index) => items.indexOf(item) === index);

const mapGridColumn = (column: string): string => {
    return column === PRODUCT_TRASH_RESOLVED_COLUMN ? PRODUCT_TRASH_RESOLVED_AT_COLUMN : column;
};

const mapResolvedFilter = (filter: GridFilterType): GridFilterType[] => {
    const values = Array.isArray(filter.value) ? filter.value : [filter.value];
    const hasResolved = values.includes('1');
    const hasUnresolved = values.includes('0');

    if (hasResolved === hasUnresolved) {
        return [];
    }

    return [
        {
            column: PRODUCT_TRASH_RESOLVED_AT_COLUMN,
            operator: hasResolved ? 'notnull' : 'null',
            value: hasResolved ? '1' : '0',
        },
    ];
};

export const mapProductTrashFilters = (filters: GridFilterType[] = []): GridFilterType[] => {
    return filters.flatMap((filter) => {
        if (filter.column === PRODUCT_TRASH_RESOLVED_COLUMN) {
            return mapResolvedFilter(filter);
        }

        return [{ ...filter, column: mapGridColumn(filter.column) }];
    });
};

export const mapProductTrashSort = (sort: GridSortType): GridSortType => ({
    ...sort,
    column: mapGridColumn(sort.column),
});

export const getProductTrashGridParams = (tableOptions: TablePropsOptionsType): GridRequestType => ({
    filters: [mapProductTrashFilters(tableOptions.filters)],
    sorts: [mapProductTrashSort(tableOptions.sort)],
    page: tableOptions.page,
    items: tableOptions.items,
    columns: unique([
        ...REQUIRED_PRODUCT_TRASH_GRID_COLUMNS,
        ...tableOptions.columns.map((column) => mapGridColumn(column.key)),
    ]),
});
